// Pure caption helpers for the scene-board views (Compact now, Expanded in Slice C).
// Mirrors native GetFormattedFullSceneName + SceneNumberStringIsPlainSceneNumber.
// Kept pure + localization-ready: the caller resolves `timeLabel` before calling
// composeSceneCaptionRest (raw scene.timeOfDay in Slices A/C; t(sceneTimeKey(...)) in D).

const LOCATION_ABBREV: Record<string, string> = {
  INT: 'INT.',
  EXT: 'EXT.',
  'INT/EXT': 'INT/EXT.',
};

/** Maps the web locationType token to the native period-suffixed abbreviation; '' otherwise. */
export function locationAbbrev(locationType: string): string {
  return LOCATION_ABBREV[locationType] ?? '';
}

/**
 * Native SceneNumberStringIsPlainSceneNumber + locked/unlocked rule:
 *   all-digits (/^\d+$/) + locked   -> 'muted'
 *   all-digits + unlocked           -> 'default'
 *   contains non-digits (1X, 2A)    -> 'accent'
 */
export function sceneNumberColorClass(
  numberStr: string,
  isNumberingLocked: boolean,
): 'accent' | 'muted' | 'default' {
  const isPlainNumber = /^\d+$/.test(numberStr);
  if (!isPlainNumber) return 'accent';
  return isNumberingLocked ? 'muted' : 'default';
}

/**
 * Native ShotNumberStringIsPlainShotNumber + locked/unlocked rule (QA issue 19, 2026-08-12).
 * INVERSE polarity vs scenes: shot "plain" = all-letters (either case), not all-digits (auto shot
 * numbers are letters by default; auto scene numbers are digits):
 *   all-letters, either case (/^[A-Za-z]*$/) + locked  -> 'muted'
 *   all-letters, either case + unlocked                -> 'default'
 *   contains anything else (digit, hyphen, ...)         -> 'accent'
 * CASE-INSENSITIVE (review fix round 1, 2026-08-12): native does
 * `shotNumber.ToUpperInvariant().All(char.IsUpper)` — every char is a letter, EITHER case — not
 * an uppercase-only test. ShotSettingsNumberNormalizer.cs's own comment confirms this explicitly
 * ("comparison...is case-insensitive, so accepting either case here is safe"), and the existing
 * `numberIsIllegalFor` precedent two functions away in ShotSettingsModal.tsx already uses
 * `/^[A-Za-z]+$/`, not an uppercase-only class. Reachable in practice: once numbering is locked,
 * ShotSettingsNumberNormalizer stores a typed value VERBATIM (no case-folding), so a locked shot
 * can legitimately hold a lowercase plain number.
 * Empty string is vacuously plain (mirrors StringUtilities' underlying .All() on an empty
 * sequence, which is true) — matches computeShotNumbers always handing back a resolved,
 * non-empty label before this runs downstream in practice.
 *
 * KNOWN UNITY QUIRK, reproduced verbatim (not fixed here): ShotNumberStringIsPlainShotNumber is
 * hardcoded to the letters-only test regardless of documentSettings.usedigitsforshotnumber, so
 * with digit-numbering enabled an all-digit AUTO shot number reads as "manual" and paints accent.
 * Parity with Unity is the ask (QA-19); flagged to the product owner separately.
 */
export function shotNumberClass(
  numberStr: string,
  isNumberingLocked: boolean,
): 'accent' | 'muted' | 'default' {
  const isPlainNumber = /^[A-Za-z]*$/.test(numberStr);
  if (!isPlainNumber) return 'accent';
  return isNumberingLocked ? 'muted' : 'default';
}

/**
 * The "Combined Display Format" shot label (numbering-system.md): the SCENE's number, the
 * project's separator ('' | '.' | '-', documentSettings.separator), then the SHOT's own number —
 * "3C", "3.C", "3-C". A shot number alone ("C") is ambiguous across scenes, which is why every
 * board/panel surface shows the pair.
 *
 * A plain join on purpose: both halves arrive already resolved (Unity owns numbering), so this
 * never re-derives, uppercases or renumbers either one. The manual/locked COLOR state is a separate
 * decision, and since 20260922 it is made PER HALF: ShotNumberLabel.tsx renders the same three
 * pieces as spans and asks sceneNumberColorClass about the scene half and shotNumberClass about the
 * shot half (the separator gets neither). This function stays the authority on the flattened TEXT —
 * every host's element still reads exactly what it returns.
 */
export function composeShotNumberLabel(
  sceneNumber: string,
  separator: string,
  shotNumber: string,
): string {
  return `${sceneNumber}${separator}${shotNumber}`;
}

/**
 * The caption portion AFTER the number, uppercased (native GetFormattedFullSceneName):
 *   head   = [locationAbbrev, name].filter(Boolean).join(' ')
 *   result = timeLabel ? (head ? `${head} – ${timeLabel}` : timeLabel) : head   // en-dash U+2013
 */
export function composeSceneCaptionRest(
  locationAbbrevStr: string,
  name: string,
  timeLabel: string,
): string {
  const head = [locationAbbrevStr, name].filter(Boolean).join(' ');
  const result = timeLabel ? (head ? `${head} – ${timeLabel}` : timeLabel) : head;
  return result.toUpperCase();
}

/**
 * "2 - EXT. RYBNÍK – DAY" — the board scene header's own spelling (SceneBoardExpanded's
 * `{scene.number}{rest ? ` - ${rest}` : ''}`), flattened to one string. `captionRest` is
 * composeSceneCaptionRest's output; the caller resolves timeLabel via t(sceneTimeKey(...)).
 * Empty segments drop WITH their separator, so a number-only scene reads "2".
 *
 * Note the two different dashes are deliberate, not a typo: a plain HYPHEN joins the number to
 * the caption here, while composeSceneCaptionRest joins head and time with an EN DASH (U+2013).
 * That is the board header's real spelling, and every surface that names a scene in prose (the
 * delete confirms) has to match it exactly or the dialog and the row it names visibly disagree.
 */
export function composeSceneHeaderLabel(sceneNumber: string, captionRest: string): string {
  return [sceneNumber.trim(), captionRest].filter(Boolean).join(' - ');
}

/**
 * Maps a web time-of-day token ('DAY' | 'NIGHT' | 'TWILIGHT') to its `common`
 * i18n key, or null for anything else (empty, 'Noir', unknown) so the caller
 * can fall back to the raw scene.timeOfDay string. Case-insensitive + trimmed:
 * timeOfDayToWeb (converters.ts) emits uppercase for the three known values but
 * passes unmapped native names (e.g. 'Noir') through verbatim.
 */
export function sceneTimeKey(timeOfDay: string): string | null {
  switch ((timeOfDay || '').trim().toUpperCase()) {
    case 'DAY':
      return 'sceneTimeDay';
    case 'NIGHT':
      return 'sceneTimeNight';
    case 'TWILIGHT':
      return 'sceneTimeTwilight';
    default:
      return null;
  }
}
